import React, { memo } from 'react'
import startCase from 'lodash/startCase'
import { formatRule } from 'apps/spot-quote-control-panel/components/SpotBiddingRulesTableRow'
import {
  CONTROL_OPTIONS,
  ICostControlConfig,
} from 'apps/spot-quote-control-panel/types/costControlConfig'
import {
  STRATEGY_CHOSEN,
  ISpotBiddingRule,
  SPOT_RULES_COLUMNS,
  RULE_ACTION,
  RULE_LOCATIONS,
  CONFIG_TYPE,
} from 'apps/spot-quote-control-panel/types/spotBiddingConfig'
import { Table, TableBody, TableHead, TableRow, TableCell } from 'transfix-ui'
import { SpotBiddingRulesRow } from './SpotBiddingRulesRow'

interface ISpotRulesTableProps {
  costControlConfig: ICostControlConfig[]
  strikePriceRules?: ISpotBiddingRule[]
  spotBiddingRules?: ISpotBiddingRule[]
  customRules?: string[]
  isDoNotBid?: boolean
  strategyChosen?: string
}

export const SpotRulesTable = memo<ISpotRulesTableProps>(
  ({
    costControlConfig,
    customRules,
    isDoNotBid,
    spotBiddingRules,
    strategyChosen,
    strikePriceRules,
  }) => {
    const formatCostControlOption = (controlOption: CONTROL_OPTIONS): string => {
      switch (controlOption) {
        case CONTROL_OPTIONS.quoting:
          return 'Spot Quoting'
        case CONTROL_OPTIONS.covering:
          return 'CM Target'
        case CONTROL_OPTIONS.sbr:
          return 'SBR'
        default:
          return ''
      }
    }

    const customRuleObjs = (customRules ?? []).map(ruleStr => {
      return {
        id: ruleStr,
        ruleAction: RULE_ACTION.dontBid,
        reason: ruleStr,
        isBaseline: false,
        isSameDay: false,
        isWeekend: false,
      } as ISpotBiddingRule
    })

    const isGlobalRule = (rule: ISpotBiddingRule): boolean => {
      return rule.spotBiddingConfig?.configType === CONFIG_TYPE.global
    }

    return (
      <Table>
        <TableHead>
          <TableRow tableCellType='th'>
            {Object.entries(SPOT_RULES_COLUMNS).map(([key, value]) => (
              <TableCell
                tableCellType='th'
                dataTest={`${key}-column-header`}
                key={`${key}-column-header`}>
                <div className='rule-header'>{value}</div>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {costControlConfig.map((rule, id) => {
            return (
              <SpotBiddingRulesRow
                key={id.toString()}
                id={id.toString()}
                isChecked={!isDoNotBid}
                isGlobal
                premiumOrCostApplied={rule.costStrategy}
                ruleType={startCase(rule.ruleType)}
                ruleDetail={formatCostControlOption(rule.controlOption)}
                ruleSubDetail={rule.isDrop ? 'Drop' : 'Live'}
                ruleLocation={RULE_LOCATIONS.costControlPanel}
              />
            )
          })}
          {strikePriceRules &&
            strikePriceRules.map(rule => {
              const formattedRule = formatRule(rule)
              const isChecked =
                (isDoNotBid && rule.ruleAction === 'DONT_BID') ||
                (!isDoNotBid &&
                  [STRATEGY_CHOSEN.strikePrice, STRATEGY_CHOSEN.revenuePlusMargin].includes(
                    strategyChosen ?? ''
                  ))

              return (
                <SpotBiddingRulesRow
                  key={rule.id}
                  id={rule.id}
                  isChecked={isChecked}
                  isGlobal={isGlobalRule(rule)}
                  premiumOrCostApplied={formattedRule.premium ?? ''}
                  ruleType={formattedRule.ruleType}
                  ruleDetail={formattedRule.value}
                  ruleSubDetail={formattedRule.type}
                  ruleLocation={RULE_LOCATIONS.strikePriceControlPanel}
                />
              )
            })}
          {spotBiddingRules &&
            spotBiddingRules.map(rule => {
              const formattedRule = formatRule(rule)
              const isChecked =
                (isDoNotBid && rule.ruleAction === 'DONT_BID') ||
                (!isDoNotBid && strategyChosen === STRATEGY_CHOSEN.spotControl)

              return (
                <SpotBiddingRulesRow
                  key={rule.id}
                  id={rule.id}
                  isChecked={isChecked}
                  isGlobal={isGlobalRule(rule)}
                  premiumOrCostApplied={formattedRule.premium ?? ''}
                  ruleType={formattedRule.ruleType}
                  ruleDetail={formattedRule.value}
                  ruleSubDetail={formattedRule.type}
                  ruleLocation={RULE_LOCATIONS.spotControlPanel}
                />
              )
            })}
          {customRuleObjs.length > 0 &&
            customRuleObjs.map(rule => {
              return (
                <SpotBiddingRulesRow
                  key={rule.id}
                  id={rule.id}
                  isChecked
                  isGlobal
                  premiumOrCostApplied='Do Not Bid'
                  ruleType='Backend Custom'
                  ruleDetail={rule.reason}
                  ruleLocation={RULE_LOCATIONS.customCode}
                />
              )
            })}
        </TableBody>
      </Table>
    )
  }
)
